import { useEffect, useState } from 'react'
import { apiFetch } from '../lib/api'

function timeAgo(value) {
  if (!value) return ''
  const ms = Date.now() - new Date(value).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(value).toLocaleDateString()
}

function iconForType(type) {
  switch (type) {
    case 'CHAT_MESSAGE':
    case 'GROUP_CHAT_MESSAGE':
      return '💬'
    case 'GROUP_HOST_MESSAGE':
      return '📢'
    case 'REQUEST_RECEIVED':
      return '📨'
    case 'REQUEST_ACCEPTED':
      return '✅'
    case 'REQUEST_DECLINED':
      return '❌'
    case 'REQUEST_CANCELLED':
      return '🚫'
    case 'PROFILE_VIEW':
      return '👀'
    default:
      return '🔔'
  }
}

export default function NotificationPage({ currentUser, onLogin, onAllRead, onOpenProfile }) {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])

  async function loadNotifications() {
    if (!currentUser?.id) return
    setLoading(true)
    try {
      const res = await apiFetch('/notification/me?limit=80')
      if (!res.ok) throw new Error('Failed to load notifications')
      const data = await res.json()
      setItems(Array.isArray(data.notifications) ? data.notifications : [])
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
  }, [currentUser?.id])

  useEffect(() => {
    if (!currentUser?.id) return

    let cancelled = false
    async function markAllRead() {
      await apiFetch('/notification/read-all', { method: 'POST' }).catch(() => {})
      if (!cancelled) onAllRead?.()
    }

    markAllRead()
    return () => {
      cancelled = true
    }
  }, [currentUser?.id, onAllRead])

  if (!currentUser) {
    return (
      <div className="rounded-3xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
        Please login to view notifications.
        <div className="mt-3">
          <button onClick={onLogin} className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-semibold text-white">Login</button>
        </div>
      </div>
    )
  }

  return (
    <section className="mx-auto w-full max-w-3xl">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Notifications</h2>
        <button onClick={loadNotifications} className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
          Refresh
        </button>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading notifications...</p>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white/70 p-6 text-sm text-gray-500">
          No notifications yet.
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <article
            key={item.id}
            className={`rounded-2xl border p-3 shadow-sm ${item.isRead ? 'border-gray-200 bg-white' : 'border-orange-200 bg-orange-50/70'}`}
          >
            <div className="flex items-start gap-3">
              {item.type === 'PROFILE_VIEW' && item.actor?.id ? (
                <button
                  type="button"
                  onClick={() => onOpenProfile?.(item.actor.id)}
                  className="shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300"
                  title={`View ${item.actor?.name || 'user'} profile`}
                >
                  <img
                    src={
                      item.actor?.profile?.profilePicture ||
                      `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(item.actor?.name || 'user')}`
                    }
                    alt={item.actor?.name || 'User'}
                    className="h-9 w-9 rounded-xl object-cover bg-gray-100 shadow-sm"
                  />
                </button>
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-lg shadow-sm">
                  {iconForType(item.type)}
                </div>
              )}

              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                {item.body && <p className="mt-0.5 text-sm text-gray-700">{item.body}</p>}
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  {item.actor?.name && (
                    item.type === 'PROFILE_VIEW' && item.actor?.id ? (
                      <button
                        type="button"
                        onClick={() => onOpenProfile?.(item.actor.id)}
                        className="font-semibold text-gray-700 hover:text-orange-700"
                      >
                        {item.actor.name}
                      </button>
                    ) : (
                      <span className="font-semibold text-gray-700">{item.actor.name}</span>
                    )
                  )}
                  <span>{timeAgo(item.createdAt)}</span>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}
